import { useState } from 'react';
import { httpClient } from '../api/http';
import { Alert } from '@piggytechng/component-library';
import { authPayload } from '../helpers/utils/auth-payload';
import { WithdrawForUserPayload } from '../types';
import useRolePermissions from './useRolePermissions';

const useWithdrawalActions = () => {
  const { canPerformAction } = useRolePermissions({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isWithdrawingForUser, setIsWithdrawingForUser] = useState<boolean>(false);
  const [withdrawalProcessor, setWithdrawalProcessor] = useState<string>('paystack');

  const withdrawalProcessorChange = (e: any) => {
    setWithdrawalProcessor(e);
  };
  const executeWithdrawalAction = async (action: string, withdrawal: any, password: string, callback?: () => void) => {
    try {
      setIsLoading(true);

      const auth = authPayload(password);

      let response: any;

      if (action === 'reverse') {
        response = await httpClient.post('admin/withdrawal-manager/reverse-withdrawal', {
          userId: withdrawal.awhm_agent_id,
          reference: withdrawal.awhm_rand,
          ...auth,
        });
      } else {
        response = await httpClient.post('admin/withdrawal-manager/quick-actions', {
          withdrawalId: withdrawal.awhm_rand,
          action,
          ...(withdrawalProcessor ? { processor: withdrawalProcessor } : {}),
          ...auth,
        });
      }

      if (response?.data?.status) {
        Alert.success(response.data.message || 'Action executed successfully');
        callback?.();
      } else {
        Alert.error(response?.data?.message || 'Action failed');
      }
    } catch (error: any) {
      console.log(error);
      Alert.error(error?.response?.data?.message || 'Something went wrong, please try again');
    } finally {
      setIsLoading(false);
    }
  };

  const withdrawForUser = async (
    userId: string,
    payload: WithdrawForUserPayload,
    password: string,
    callback?: () => void,
  ) => {
    if (!canPerformAction('withdrawalManager', 'withdrawForUser')) {
      Alert.warn('You do not have access to perform this action.');
      return false;
    }

    try {
      setIsWithdrawingForUser(true);

      const response = await httpClient.post<any>('admin/withdrawal-manager/withdraw-for-user', {
        userId,
        wallet: payload.wallet,
        amount: payload.amount,
        destination: payload.destination,
        ...(payload.wallet === 'piggybank' ? { penaltysource: payload.penaltysource } : {}),
        ...authPayload(password),
      });

      if (response?.data?.status) {
        Alert.success(response.data.message || 'Withdrawal initiated successfully');
        callback?.();
        return true;
      }

      Alert.error(response?.data?.message || 'Withdrawal could not be initiated');
      return false;
    } catch (error: any) {
      console.log(error?.response?.data?.message || 'Something went wrong, please try again');
      return false;
    } finally {
      setIsWithdrawingForUser(false);
    }
  };

  return {
    isLoading,
    withdrawForUser,
    isWithdrawingForUser,
    executeWithdrawalAction,
    withdrawalProcessor,
    setWithdrawalProcessor,
    withdrawalProcessorChange,
  };
};

export default useWithdrawalActions;
